import { useEffect, useRef, useState } from 'react';

const readAccessTokenFromStorage = () => {
  return localStorage.getItem('access_token') || '';
};

const resolveWsUrl = ({ token }) => {
  if (process.env.REACT_APP_WS_URL) {
    const base = process.env.REACT_APP_WS_URL.replace(/\/$/, '');
    return `${base}/notifications/?token=${encodeURIComponent(token)}`;
  }

  const apiUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_URL_DEV || process.env.REACT_APP_API_URL_PROD;
  if (apiUrl && /^https?:\/\//i.test(apiUrl)) {
    const wsBase = apiUrl
      .replace(/^http:/i, 'ws:')
      .replace(/^https:/i, 'wss:')
      .replace(/\/api\/?$/i, '')
      .replace(/\/$/, '');
    return `${wsBase}/notifications/?token=${encodeURIComponent(token)}`;
  }

  // Default: same-origin websocket.
  // If frontend is running on a dev port (3000/3001), assume backend ASGI is on 8000.
  const { protocol, hostname, port } = window.location;
  const wsProtocol = protocol === 'https:' ? 'wss:' : 'ws:';

  const backendPort = port === '3000' || port === '3001' ? '8000' : port;
  const wsOrigin = backendPort ? `${wsProtocol}//${hostname}:${backendPort}` : `${wsProtocol}//${hostname}`;

  return `${wsOrigin}/notifications/?token=${encodeURIComponent(token)}`;
};

export const useNotificationsWebSocket = ({ enabled = true, token: providedToken, onMessage } = {}) => {
  const [status, setStatus] = useState('idle');
  const [token, setToken] = useState(() => providedToken || readAccessTokenFromStorage());
  const wsRef = useRef(null);

  const log = (...args) => {
    console.log('[GLOW][ws][notifications]', ...args);
  };

  const maskToken = (t) => {
    if (!t) return '';
    if (t.length <= 12) return '***';
    return `${t.slice(0, 6)}...${t.slice(-4)}`;
  };

  useEffect(() => {
    if (providedToken !== undefined) {
      setToken(providedToken || '');
      return;
    }

    // Same-tab token changes do not trigger the 'storage' event. Use a lightweight poll.
    const intervalId = window.setInterval(() => {
      const next = readAccessTokenFromStorage();
      setToken((prev) => (prev === next ? prev : next));
    }, 2000);

    return () => window.clearInterval(intervalId);
  }, [providedToken]);

  useEffect(() => {
    if (!enabled) return;
    if (!token) return;

    const url = resolveWsUrl({ token });
    log('connecting', { url, token: maskToken(token) });
    setStatus('connecting');

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      log('open');
      setStatus('open');
    };

    ws.onclose = (event) => {
      log('close', { code: event?.code, reason: event?.reason });
      setStatus('closed');
    };

    ws.onerror = (event) => {
      log('error', event);
      setStatus('error');
    };

    ws.onmessage = (event) => {
      if (!event?.data) return;

      try {
        const payload = JSON.parse(event.data);
        log('message', payload?.event || 'unknown');
        if (onMessage) onMessage(payload);
      } catch (_e) {
        // ignore invalid JSON
      }
    };

    return () => {
      try {
        ws.close();
      } catch (_e) {
        // ignore
      }
    };
  }, [enabled, onMessage, token]);

  const close = () => {
    const ws = wsRef.current;
    if (ws) {
      try {
        ws.close();
      } catch (_e) {
        // ignore
      }
    }
  };

  return { status, close };
};
